/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  ClickAwayListener,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Grow,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem,
  MenuList,
  MenuProps,
  Paper,
  Popper,
  Tab,
  Tabs,
  Typography,
  withStyles
} from '@material-ui/core';
import { DecisionDialog, LoaderBar, Page } from 'src/components';
import { cloneDeep, isEqual } from 'lodash';
import { slices, useAppDispatch } from 'src/redux';
import { useFormik } from 'formik';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';

import useUserInfo from 'src/hooks/user/use-user-info';
import {
  PurchaseOrderFormValues,
  UpdatePurchaseOrderPayload
} from 'src/redux/slices/purchase-order/types';
import { PurchaseOrderForm } from './components/PurchaseOrderFormV2';
import { unwrapResult } from '@reduxjs/toolkit';
import { usePermissions, useRoles, useSnackBar } from 'src/hooks';
import {
  allowedStatusToViewCheque,
  purchaseOrderRoles,
  PurchaseOrderStatusesV2,
  purchaseOrderStatusOptionsV2,
  roles
} from 'src/redux/slices/purchase-order';
import { useNavigate, useParams } from 'react-router';
import {
  dataCleanerForPurchaseOrderUpdate,
  getPurchaseOrderResponseFormikTransformer,
  isPurchaseOrderProductValid
} from 'src/redux/slices/purchase-order/utils';
import PurchaseOrderPDFDialog from './components/PurchaseOrderPDFDialog';
import PurchaseOrderStatusField from './components/PurchaseOrderStatusSelect';
import usePrompt from 'src/utils/navigation-prompt';
import RoleChip from 'src/components/chips/RoleChip';
import { isStatusAllowedForUser } from 'src/utils/purchase-order/check-po-access';
import useResolution from 'src/hooks/useResolution';
import ReceiptIcon from '@material-ui/icons/Receipt';
import ShoppingBasketIcon from '@material-ui/icons/ShoppingBasket';
import { ChequeForm } from './components/ChequeForm';
import { pdf } from '@react-pdf/renderer';
import PurchaseOrderPDFDocument from './components/dialog/PurchaseOrderPDFDocument';
import { saveAs } from 'file-saver';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { openInNewTab } from 'src/utils';
import ListItemText from '@material-ui/core/ListItemText';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CancelScheduleSendIcon from '@material-ui/icons/CancelScheduleSend';

const { actions: purchaseOrderActions } = slices.purchaseOrder;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
    marginTop: 3
  }
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    color: theme.palette.text.primary
  }
}))(MenuItem);

export const component = () => {
  const { id } = useParams();
  const { userDetails } = useUserInfo();
  const { branchPONickNameViaID } = useBranchInfo();
  const { isMobile, isIPadAir } = useResolution();
  const classes = useStyles();
  const navigate = useNavigate();
  const snackbar = useSnackBar();
  const dispatch = useAppDispatch();

  const {
    canUpdatePurchaseOrder,
    canDeletePurchaseOrder,
    canViewProductInPO
  } = usePermissions();

  const [originalPurchaseOrder, setOriginalPurchaseOrderDetails] = useState<
    PurchaseOrderFormValues
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPdfDialogVisible, setIsPdfDialogVisible] = useState<boolean>(false);
  const [currentStatusValue, setCurrentStatusValue] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [cancelPoDialog, setCancelPoDialog] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isDraft = useMemo(
    () => currentStatusValue === PurchaseOrderStatusesV2.DRAFT,
    [currentStatusValue]
  );

  const isForDelivery = useMemo(() => currentStatusValue === 'for_delivery', [
    currentStatusValue
  ]);

  const isOwner = useMemo(() => {
    return userDetails?.all_roles?.some((role) => role.name === 'owner');
  }, [userDetails]);

  const canCancelPO = useMemo(
    () =>
      userDetails?.all_roles?.some(
        (role) =>
          role.name === 'owner' ||
          role.name === 'Purchasing Product Specialist' ||
          role.name === 'Purchasing Product Specialist II'
      ),
    [userDetails]
  );

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  // Maybe pwede idebounce to prevent multiple calls.
  // const onProductsAdd = async () => {
  //   const clonedValues = cloneDeep(formikRef?.values);

  //   const newAddedProducts = clonedValues?.products?.filter(
  //     (x) => !x?.purchase_order_product_id
  //   );
  //   // Has new products
  //   if (newAddedProducts && newAddedProducts?.length > 0) {
  //     const addProductResp = unwrapResult(
  //       await dispatch(
  //         purchaseOrderActions.updatePurchaseOrderProductsThunk({
  //           products: dataCleanerForPurchaseOrderProductsUpdate(
  //             newAddedProducts
  //           ),
  //           po_id: clonedValues?.po_id
  //         })
  //       )
  //     );
  //   }

  //   // TODO: Check for updated products
  //   // TODO: Check for deleted products
  // };

  // const onProductsDelete = async () => {
  //   const clonedValues = cloneDeep(formikRef?.values);

  //   const newAddedProducts = clonedValues?.products?.filter(
  //     (x) => !x?.purchase_order_product_id
  //   );
  //   // Has new products
  //   if (newAddedProducts && newAddedProducts?.length > 0) {
  //     const addProductResp = unwrapResult(
  //       await dispatch(
  //         purchaseOrderActions.updatePurchaseOrderProductsThunk({
  //           products: dataCleanerForPurchaseOrderProductsUpdate(
  //             newAddedProducts
  //           ),
  //           po_id: clonedValues?.po_id
  //         })
  //       )
  //     );
  //   }

  //   // TODO: Check for updated products
  //   // TODO: Check for deleted products
  // };

  // const onChangeProducts = debounce(() => {
  //   const clonedValues = cloneDeep(formikRef?.values);
  //   const products = clonedValues?.products;

  //   // TODO: Need validation
  //   // Products length should be > 0
  //   // Products quantity should be > 0
  //   // Products product_id should be not null
  //   // Products product_name should be not null

  //   /** For new products */
  //   const newAddedProducts = clonedValues?.products?.filter(
  //     (x) => !x?.purchase_order_product_id
  //   );
  //   if (newAddedProducts && newAddedProducts?.length > 0) {
  //     onProductsAdd();
  //   }

  //   /**For Deleted Products */

  //   console.log(products);
  // }, 1000);

  // const processProducts = () => {
  //   const clonedValues = cloneDeep(formikRef?.values);

  //   const newAddedProducts = clonedValues?.products?.filter(
  //     (x) => !x?.purchase_order_product_id
  //   );
  //   // Has new products
  //   if (newAddedProducts && newAddedProducts?.length > 0) {
  //     onProductsAdd();
  //   }

  // };

  const areProductsValid = () => {
    const products = cloneDeep(formikRef?.values?.products);
    if (!products) {
      snackbar.show({
        message: `products undefined`,
        severity: 'error',
        useSound: true
      });
      return;
    }
    if (!Array.isArray(products) || products.length === 0) {
      snackbar.show({
        message: `products undefined`,
        severity: 'error',
        useSound: true
      });
      return;
    }

    let allProductsValid = true;
    for (const product of products) {
      const { valid, message, index } = isPurchaseOrderProductValid(
        product,
        products.indexOf(product)
      );
      if (!valid) {
        formikRef.setFieldValue(`products[${index}].valid`, false);
        formikRef.setFieldValue(`products[${index}].message`, message);
        allProductsValid = false;
      }
    }
    if (!allProductsValid) {
      return;
    }

    return true;
  };

  const onClickUpdate = async (values: UpdatePurchaseOrderPayload) => {
    if (isEqual(values, originalPurchaseOrder)) {
      snackbar.show({
        message: `No changes detected`,
        severity: 'error',
        useSound: true
      });
      return;
    }

    if (!areProductsValid()) {
      return;
    }

    setIsLoading(true);

    const newValues = {
      ...values,
      is_owner: isOwner ? 1 : 0,
      ...(values.terms
        ? { terms: `${parseInt(values.terms.toString())} days` }
        : undefined)
    };

    const { products, ...restValues } = newValues;

    //change update props from original_dealer_price to dealer_price
    const cleanedProducts = products?.map(
      ({ original_dealer_price, ...restProduct }) => ({
        ...restProduct
      })
    );

    // Need ng dalawang value na to para macompare yung changes sa thunk para maapply changes
    const clonedNewValue = dataCleanerForPurchaseOrderUpdate({
      ...restValues,
      products: cleanedProducts
    });
    // NEW VALUES
    const clonedOldValues: any = dataCleanerForPurchaseOrderUpdate(
      // OLD VALUES
      originalPurchaseOrder
    ); // TODO: Fix this `ANY` type
    const response = unwrapResult(
      await dispatch(
        purchaseOrderActions.updatePurchaseOrderThunk({
          newValue: clonedNewValue,
          oldValue: clonedOldValues
        })
      )
    );

    if (response?.success && response?.originalData?.data) {
      snackbar.show({
        message: response?.message || 'Purchase Order Details Updated',
        severity: 'success'
      });
      // processProducts(); // Important. This is to process products
    } else {
      const errors = Object.values(response?.errors || {});
      const message = response?.message ?? 'Failed to update Purchase Order';
      snackbar.show({
        message: errors ? errors.join(', ') : message,
        severity: 'error'
      });
    }
    setIsLoading(false);
    fetchPurchaseOrderDetails();
  };

  const fetchPurchaseOrderDetails = async () => {
    setIsLoading(true);

    const response = unwrapResult(
      await dispatch(
        purchaseOrderActions.getPurchaseOrderDetailsThunk(Number(id))
      )
    );

    if (response?.success && response?.originalData?.data) {
      const responseData = getPurchaseOrderResponseFormikTransformer(
        response.originalData.data
      );
      setOriginalPurchaseOrderDetails(responseData);
      setCurrentStatusValue(responseData?.status || '');
      formikRef.setValues(responseData, false);
    } else {
      snackbar.show({
        message: `${response?.message || 'Failed to fetch Purchase Order'}, ${
          response?.errors ? JSON.stringify(response?.errors) : ''
        }`,
        severity: 'error'
      });
    }
    setIsLoading(false);
  };

  const formSchema = Yup.object().shape({
    branch_id: Yup.number().required('Branch is required'),
    supplier_id: Yup.number().required('Supplier is required'),
    products: Yup.array().min(1, 'At least one products is required')
  });

  const formikRef = useFormik({
    initialValues: originalPurchaseOrder,
    enableReinitialize: true,
    onSubmit: canUpdatePurchaseOrder ? onClickUpdate : () => {},
    validationSchema: formSchema,
    validateOnChange: true
  });

  const handleSubmitWithValidation = async () => {
    const errors = await formikRef.validateForm();
    const errorsArr = Object.entries(errors) ?? [];

    if (errorsArr.length > 0) {
      snackbar.show({
        message: `${errorsArr[0][1]}`,
        severity: 'error'
      });
    } else {
      formikRef.handleSubmit(); // Triggers onSubmit
    }
  };

  const handleCancelOrder = useCallback(
    async (id: number) => {
      try {
        const response = unwrapResult(
          await dispatch(purchaseOrderActions.cancelPurchaseOrderThunk(id))
        );

        if (response?.success) {
          snackbar.show({
            message:
              response?.message || 'Purchase Order successfully cancelled',
            severity: 'success'
          });
          window.location.reload();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, snackbar]
  );

  const onDownloadPurchaseOrder = async () => {
    const branchName = branchPONickNameViaID(
      originalPurchaseOrder?.branch_id || 0
    ); // 0 is impossible
    const fileName = `${branchName} ${originalPurchaseOrder?.order_no}.pdf`;
    const blob = await pdf(
      <PurchaseOrderPDFDocument purchaseOrderData={originalPurchaseOrder} />
    ).toBlob();
    saveAs(blob, fileName);
  };

  useEffect(() => {
    fetchPurchaseOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   if (!formikRef?.values?.products) {
  //     return;
  //   }
  //   onChangeProducts();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [formikRef?.values?.products]);

  usePrompt(
    'There are some unsaved changes. Are you sure you want to leave?',
    formikRef.dirty // true if has changes
  );

  const userRoles = useMemo(() => {
    const userRoleNames =
      userDetails?.all_roles?.map((role) => role.name) || [];

    // coded like this for ts error
    const filteredRoles: string[] = [];
    userRoleNames.forEach((role) => {
      if (role) {
        filteredRoles.push(role);
      }
    });

    return filteredRoles;
  }, [userDetails]);

  const isAllowed = useMemo(() => {
    return isStatusAllowedForUser(userRoles, currentStatusValue);
  }, [currentStatusValue, userRoles]);

  useEffect(() => {
    // Only check access after loading is complete and we have all data
    if (!isLoading && currentStatusValue && userRoles?.[0]) {
      if (!isAllowed) {
        setOpenDialog(true);
      }
    }
  }, [isAllowed, isLoading, currentStatusValue, userRoles]);

  return (
    <Page
      className={classes.root}
      title={`${formikRef.values?.order_no || 'Purchase Order'}`}
    >
      {isAllowed ? (
        <Container maxWidth={false}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item md={6} sm={6} xs={12}>
              <Grid container alignItems="center" spacing={2}>
                <Typography color="textPrimary" gutterBottom variant="h3">
                  Purchase Order: {isMobile ? <br /> : ' '}{' '}
                  {formikRef.values?.order_no}
                </Typography>
                {!isLoading && (
                  <RoleChip roles={roles} roleOptions={purchaseOrderRoles} />
                )}
              </Grid>
            </Grid>

            <Grid
              item
              md={6}
              sm={6}
              xs={12}
              style={{
                marginTop: isMobile ? 10 : 0,
                marginBottom: isMobile ? 10 : 0
              }}
            >
              <Box
                display="flex"
                justifyContent={isMobile ? 'flex-end' : 'flex-end'}
                style={{ gap: 3, marginTop: isMobile ? 10 : 0 }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  alignItems="center"
                  wrap="nowrap"
                >
                  <Grid item xs="auto">
                    {isLoading ? null : (
                      <div>
                        <Button
                          aria-controls="customized-menu"
                          aria-haspopup="true"
                          variant="outlined"
                          onClick={handleClick}
                          style={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          P.O. Utilities
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              paddingBottom: 3,
                              marginLeft: 4
                            }}
                          >
                            {anchorEl ? (
                              <ExpandLessIcon style={{ fontSize: 20 }} />
                            ) : (
                              <ExpandMoreIcon style={{ fontSize: 20 }} />
                            )}
                          </span>
                        </Button>

                        <StyledMenu
                          id="customized-menu"
                          anchorEl={anchorEl}
                          keepMounted
                          open={Boolean(anchorEl)}
                          onClose={handleClose}
                        >
                          {!isLoading &&
                            canViewProductInPO &&
                            ![
                              'draft',
                              'approved',
                              'for_approval',
                              'declined'
                            ].includes(currentStatusValue) && (
                              <StyledMenuItem
                                onClick={() => {
                                  navigate(
                                    `/app/product-in/${originalPurchaseOrder?.po_id}`
                                  );
                                  handleClose();
                                }}
                              >
                                <ListItemIcon>
                                  <AddIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="MANAGE P.O. ITEMS" />
                              </StyledMenuItem>
                            )}

                          <StyledMenuItem
                            onClick={() => {
                              setIsPdfDialogVisible(true);
                              handleClose();
                            }}
                          >
                            <ListItemIcon>
                              <PrintIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="PRINT P.O. FORM" />
                          </StyledMenuItem>

                          <StyledMenuItem
                            onClick={() => {
                              onDownloadPurchaseOrder();
                              handleClose();
                            }}
                          >
                            <ListItemIcon>
                              <GetAppIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary="DOWNLOAD P.O. FORM" />
                          </StyledMenuItem>

                          <StyledMenuItem
                            onClick={() => {
                              openInNewTab(
                                `/app/purchase-order/add?dup_po_id=${formikRef.values?.po_id}`
                              );
                              handleClose();
                            }}
                          >
                            <ListItemIcon>
                              <FileCopyOutlinedIcon
                                fontSize="small"
                                color="primary"
                              />
                            </ListItemIcon>
                            <ListItemText primary="RECREATE THIS FORM" />
                          </StyledMenuItem>
                        </StyledMenu>
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <Box style={{ marginTop: isMobile ? 10 : isIPadAir ? 15 : 20 }}>
            <Typography
              variant="h5"
              color="textSecondary"
              style={{ fontWeight: 500 }}
            >
              Date Created at {formikRef.values?.po_date}
            </Typography>
          </Box>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginTop: 20
            }}
          >
            <PurchaseOrderStatusField
              id="status"
              fullWidth={false}
              value={formikRef?.values?.status}
              currentValue={currentStatusValue}
              onChange={(v) =>
                formikRef?.setFieldValue('status', v?.value, true)
              }
              onBlur={formikRef?.handleBlur}
              isDetailsPage={true}
            />
          </div>
          <LoaderBar isLoading={isLoading} />
          {/* TODO: This is depracated keep comment for version 1 reference */}
          {/* <Box mt={4} style={{ opacity: isLoading ? 0.3 : 1 }}>
            <>
              <Tabs
                centered
                variant="fullWidth"
                value={tabValue}
                style={{ marginBottom: '1rem' }}
                onChange={handleTabChange}
              >
                <Tab icon={<ShoppingBasketIcon />} label="Purchase Order" />
                <Tab
                  icon={<ReceiptIcon />}
                  label="Cheque Details"
                  disabled={
                    !allowedStatusToViewCheque.includes(currentStatusValue)
                  }
                />
              </Tabs>
              {tabValue === 0 && (
                <PurchaseOrderForm
                  formikRef={formikRef}
                  isEditable={isDraft}
                  isForDelivery={isForDelivery}
                  currentStatusValue={currentStatusValue}
                />
              )}
              {tabValue === 1 && (
                <ChequeForm
                  formikRef={formikRef}
                  isEditable={isDraft}
                  isForDelivery={isForDelivery}
                  currentStatusValue={currentStatusValue}
                />
              )}
            </>
          </Box> */}
          <Box mt={4} style={{ opacity: isLoading ? 0.3 : 1 }}>
            <PurchaseOrderForm
              formikRef={formikRef}
              isEditable={isDraft}
              isForDelivery={isForDelivery}
              currentStatusValue={currentStatusValue}
              originalSINo={originalPurchaseOrder?.si_no ?? null}
            />
          </Box>
          <Grid
            container
            item
            xs={12}
            style={{ marginTop: 10 }}
            spacing={2}
            alignItems="center"
          >
            {/* Cancel button on the left (if allowed) */}
            {(canDeletePurchaseOrder || canCancelPO) &&
              currentStatusValue !== PurchaseOrderStatusesV2.CANCEL && (
                <Grid item xs={12} md="auto">
                  <Button
                    fullWidth
                    color="secondary"
                    style={{ paddingTop: 16, paddingBottom: 16 }}
                    variant="outlined"
                    onClick={() => setCancelPoDialog(true)}
                    startIcon={
                      <CancelScheduleSendIcon style={{ marginBottom: 4 }} />
                    }
                  >
                    Cancel Purchase Order
                  </Button>
                </Grid>
              )}

            {/* Update button always pushed to the right */}
            {canUpdatePurchaseOrder && (
              <Grid item xs={12} md="auto" style={{ marginLeft: 'auto' }}>
                <Button
                  fullWidth
                  color="primary"
                  size="large"
                  style={{ paddingTop: 16, paddingBottom: 16 }}
                  type="submit"
                  variant="contained"
                  onClick={handleSubmitWithValidation}
                >
                  Update Purchase Order
                </Button>
              </Grid>
            )}
          </Grid>
        </Container>
      ) : null}

      <PurchaseOrderPDFDialog
        purchaseOrderData={originalPurchaseOrder}
        isPdfDialogVisible={isPdfDialogVisible}
        onClose={() => setIsPdfDialogVisible(false)}
      />

      <DecisionDialog
        isOpen={cancelPoDialog}
        title="Cancel Purchase Order"
        subTitle="Are you sure you want to cancel this Purchase Order."
        onHandleConfirmAction={() => handleCancelOrder(Number(id))}
        onHandleClose={() => setCancelPoDialog(false)}
      />

      <Dialog open={openDialog} aria-labelledby="access-denied-dialog-title">
        <DialogTitle id="access-denied-dialog-title">Access Denied</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            You do not have permission to view this Purchase Order.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              window.location.href = '/app/purchase-order';
            }}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export const PurchaseOrderDetailsView = React.memo(component);
